import { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useNavigate } from 'react-router-dom';

import SoftBadge from 'components/SoftBadge';
import SoftButton from 'components/SoftButton';
import SoftBox from 'components/SoftBox';
import SoftInput from 'components/SoftInput';
import { showSuiErrorSnackbar } from 'components/SuiSnackbar';
import SoftTypography from 'components/SoftTypography';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DefaultBackgroundCard from 'examples/Cards/BackgroundCards/DefaultBackgroundCard';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';

import { useAuth } from 'auth';
import { useLocale } from 'lib/i18n/client';
import { getTranslations, interpolateTemplate } from 'lib/i18n/dictionary';
import {
  createServiceRequest,
  type ServicePriceCurrency,
  fetchProfileServices,
  fetchServices,
  readCachedProfileServices,
  readCachedServices,
  type ServicePriceMode,
  type ServicePriceUnit,
  type ServiceSummary,
} from 'service';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY_CODE } from 'currency/currency';

const PRICE_UNIT_LABELS: Record<Exclude<ServicePriceUnit, 'CUSTOM' | 'PROJECT'>, string> = {
  DAY: 'day',
  HOUR: 'hour',
  ITEM: 'item',
  MONTH: 'month',
  SESSION: 'session',
  WEEK: 'week',
};

function formatCurrency(value: number, currency: ServicePriceCurrency): string {
  if (!Number.isFinite(value)) {
    return '0.00';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

function getPriceUnitLabel(service: ServiceSummary): string | null {
  if (!service.priceUnit) {
    return null;
  }

  if (service.priceUnit === 'CUSTOM') {
    const customUnitLabel = service.priceUnitCustom?.trim();
    return customUnitLabel?.length ? customUnitLabel : null;
  }

  if (service.priceUnit === 'PROJECT') {
    return null;
  }

  return PRICE_UNIT_LABELS[service.priceUnit] ?? null;
}

function formatServicePricing(service: ServiceSummary): string {
  const priceMinLabel = formatCurrency(service.priceMin, service.priceCurrency);
  const priceMaxLabel =
    typeof service.priceMax === 'number'
      ? formatCurrency(service.priceMax, service.priceCurrency)
      : null;

  let amountLabel = priceMinLabel;
  if (service.priceMode === 'FROM') {
    amountLabel = `From ${priceMinLabel}`;
  } else if (service.priceMode === 'RANGE') {
    amountLabel = priceMaxLabel
      ? `${priceMinLabel} - ${priceMaxLabel}`
      : `${priceMinLabel} - ${priceMinLabel}`;
  }

  const priceUnitLabel = getPriceUnitLabel(service);
  return priceUnitLabel ? `${amountLabel} / ${priceUnitLabel}` : amountLabel;
}

function getPriceModeLabel(
  priceMode: ServicePriceMode,
  labels: {
    from: string;
    range: string;
    fixed: string;
  }
): string {
  if (priceMode === 'FROM') {
    return labels.from;
  }

  if (priceMode === 'RANGE') {
    return labels.range;
  }

  return labels.fixed;
}

function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 3).trimEnd()}...`;
}

function getServiceSummaryText(service: ServiceSummary): string {
  return (
    service.shortDescription?.trim() ||
    service.description?.trim() ||
    'No short description'
  );
}

function getServiceMediaUrl(service: ServiceSummary): string | null {
  const imageMedia = service.medias?.find(
    (media) => media.mediaType === 'IMAGE' && media.publicUrl?.trim()
  );
  return imageMedia?.publicUrl?.trim() || null;
}

function Services() {
  const { account, accessToken, activeProfileUuid, isProfilesLoading } = useAuth();
  const navigate = useNavigate();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const serviceListTranslations = translations.servicesPage;
  const serviceEditorTranslations = translations.adminServiceEditor;
  const cachedServices =
    account?.uuid && activeProfileUuid
      ? readCachedProfileServices(activeProfileUuid)
      : !account?.uuid
        ? readCachedServices()
        : undefined;
  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [description, setDescription] = useState('');
  const [priceMode, setPriceMode] = useState<ServicePriceMode>('FIXED');
  const [priceCurrency, setPriceCurrency] = useState<ServicePriceCurrency>(DEFAULT_CURRENCY_CODE);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [priceUnit, setPriceUnit] = useState<ServicePriceUnit | ''>('');
  const [priceUnitCustom, setPriceUnitCustom] = useState('');
  const [services, setServices] = useState<ServiceSummary[]>(() => cachedServices ?? []);
  const [isLoading, setIsLoading] = useState(() => cachedServices === undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (submitErrorMessage) {
      showSuiErrorSnackbar(submitErrorMessage);
    }
  }, [submitErrorMessage]);

  const priceMinPlaceholder =
    priceMode === 'FIXED'
      ? serviceEditorTranslations.pricePlaceholder
      : priceMode === 'FROM'
        ? serviceEditorTranslations.fromPricePlaceholder
        : serviceEditorTranslations.minimumPricePlaceholder;
  const priceMinValidationLabel =
    priceMode === 'FIXED'
      ? serviceEditorTranslations.priceLabel
      : serviceEditorTranslations.minimumPriceLabel;

  const loadProviderServices = async (
    providerUuid: string,
    token?: string
  ): Promise<ServiceSummary[]> =>
    fetchProfileServices(providerUuid, token);

  const reloadActiveProviderServices = async (providerUuid: string) => {
    const providerServices = await loadProviderServices(
      providerUuid,
      accessToken ?? undefined
    );
    setServices(providerServices);
  };

  useEffect(() => {
    if (account?.uuid && isProfilesLoading) {
      setIsLoading(true);
      return;
    }

    let isCancelled = false;

    async function loadServices() {
      const cachedData =
        account?.uuid && activeProfileUuid
          ? readCachedProfileServices(activeProfileUuid)
          : !account?.uuid
            ? readCachedServices()
            : undefined;

      if (!isCancelled && cachedData !== undefined) {
        setServices(cachedData);
        setIsLoading(false);
      } else if (!isCancelled) {
        setIsLoading(true);
      }

      setErrorMessage('');
      setSubmitErrorMessage('');

      try {
        if (account?.uuid) {
          if (!activeProfileUuid) {
            if (!isCancelled) {
              setServices([]);
            }
            return;
          }

          const providerServices = await loadProviderServices(
            activeProfileUuid,
            accessToken ?? undefined
          );

          if (!isCancelled) {
            setServices(providerServices);
          }

          return;
        }

        const allServices = await fetchServices(accessToken ?? undefined);
        if (!isCancelled) {
          setServices(allServices);
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : serviceListTranslations.adminLoadFailed
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadServices();

    return () => {
      isCancelled = true;
    };
  }, [
    account?.uuid,
    accessToken,
    activeProfileUuid,
    isProfilesLoading,
    serviceListTranslations.adminLoadFailed,
  ]);

  const handleOpenCreateDialog = () => {
    setSubmitErrorMessage('');
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (isSubmitting) {
      return;
    }

    setIsCreateDialogOpen(false);
  };

  const handleCreateService = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedTitle = title.trim();
    const normalizedShortDescription = shortDescription.trim();
    const normalizedSeoDescription = seoDescription.trim();
    const normalizedDescription = description.trim();
    const normalizedPriceUnitCustom = priceUnitCustom.trim();
    const numericPriceMin = Number(priceMin);
    const numericPriceMax = Number(priceMax);

    if (!activeProfileUuid) {
      setSubmitErrorMessage(serviceListTranslations.adminProfileMissing);
      return;
    }

    if (normalizedTitle.length < 2) {
      setSubmitErrorMessage(serviceEditorTranslations.titleValidation);
      return;
    }

    if (normalizedShortDescription.length > 280) {
      setSubmitErrorMessage(serviceEditorTranslations.shortDescriptionMax);
      return;
    }

    if (normalizedSeoDescription.length > 320) {
      setSubmitErrorMessage(serviceEditorTranslations.seoDescriptionMax);
      return;
    }

    if (!Number.isFinite(numericPriceMin) || numericPriceMin < 0) {
      setSubmitErrorMessage(
        interpolateTemplate(serviceEditorTranslations.priceValidation, {
          label: priceMinValidationLabel,
        })
      );
      return;
    }

    if (priceMode === 'RANGE') {
      if (!Number.isFinite(numericPriceMax) || numericPriceMax < 0) {
        setSubmitErrorMessage(serviceEditorTranslations.maximumPriceValidation);
        return;
      }

      if (numericPriceMax < numericPriceMin) {
        setSubmitErrorMessage(serviceEditorTranslations.maximumPriceRangeValidation);
        return;
      }
    }

    if (priceUnit === 'CUSTOM' && !normalizedPriceUnitCustom) {
      setSubmitErrorMessage(serviceEditorTranslations.customUnitRequired);
      return;
    }

    if (priceUnit !== 'CUSTOM' && normalizedPriceUnitCustom) {
      setSubmitErrorMessage(serviceEditorTranslations.customUnitInvalid);
      return;
    }

    setSubmitErrorMessage('');
    setIsSubmitting(true);

    try {
      await createServiceRequest(
        {
          profileUuid: activeProfileUuid,
          title: normalizedTitle,
          shortDescription: normalizedShortDescription || undefined,
          seoDescription: normalizedSeoDescription || undefined,
          description: normalizedDescription || undefined,
          priceMode,
          priceCurrency,
          priceMin: numericPriceMin,
          priceMax: priceMode === 'RANGE' ? numericPriceMax : undefined,
          priceUnit: priceUnit || undefined,
          priceUnitCustom: normalizedPriceUnitCustom || undefined,
        },
        accessToken ?? undefined
      );

      setTitle('');
      setShortDescription('');
      setSeoDescription('');
      setDescription('');
      setPriceMode('FIXED');
      setPriceCurrency(DEFAULT_CURRENCY_CODE);
      setPriceMin('');
      setPriceMax('');
      setPriceUnit('');
      setPriceUnitCustom('');
      setIsCreateDialogOpen(false);
      await reloadActiveProviderServices(activeProfileUuid);
    } catch (error) {
      setSubmitErrorMessage(
        error instanceof Error ? error.message : serviceEditorTranslations.createFailed
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <SoftBox
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  gap={2}
                  mb={account?.uuid && !isProfilesLoading && !activeProfileUuid ? 2 : 0}
                >
                  <SoftBox lineHeight={1}>
                    <SoftTypography variant="h5" fontWeight="medium">
                      {serviceListTranslations.title}
                    </SoftTypography>
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                      {serviceListTranslations.adminListDescription}
                    </SoftTypography>
                  </SoftBox>
                  <SoftButton
                    variant="gradient"
                    color="info"
                    onClick={handleOpenCreateDialog}
                    disabled={isProfilesLoading || !activeProfileUuid}
                  >
                    {serviceListTranslations.adminCreateAction}
                  </SoftButton>
                </SoftBox>
                {!account?.uuid ? (
                  <SoftTypography variant="button" color="text">
                    {serviceListTranslations.adminAccountMissing}
                  </SoftTypography>
                ) : !isProfilesLoading && !activeProfileUuid ? (
                  <SoftTypography variant="button" color="text">
                    {serviceListTranslations.adminProfileMissing}
                  </SoftTypography>
                ) : null}
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <SoftBox>
              {isLoading ? (
                <SoftTypography variant="button" color="text">
                  {serviceListTranslations.adminLoading}
                </SoftTypography>
              ) : services.length === 0 ? (
                <SoftTypography variant="button" color="text">
                  {serviceListTranslations.adminEmpty}
                </SoftTypography>
              ) : (
                <Grid container spacing={3}>
                  {services.map((service) => {
                    const serviceSummary = getServiceSummaryText(service);
                    const serviceMediaUrl = getServiceMediaUrl(service);
                    const serviceEditPath = `/services/${service.uuid}/edit`;
                    return (
                      <Grid item xs={12} md={6} xl={4} key={service.uuid}>
                        <SoftBox display="flex" flexDirection="column" height="100%">
                          <DefaultBackgroundCard
                            compact
                            color="info"
                            overlay={!serviceMediaUrl}
                            image={serviceMediaUrl ?? ''}
                            title={service.title}
                            description={truncateText(serviceSummary, 110)}
                            badge={formatServicePricing(service)}
                            action={
                              {
                                type: 'internal',
                                route: serviceEditPath,
                              } as never
                            }
                          />
                          <SoftBox
                            px={0.5}
                            pt={2}
                            display="flex"
                            flexDirection="column"
                            flexGrow={1}
                          />
                        </SoftBox>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
      <Dialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{serviceEditorTranslations.createTitle}</DialogTitle>
        <SoftBox component="form" onSubmit={handleCreateService}>
          <DialogContent>
            <Stack spacing={2}>
              <SoftInput
                placeholder={serviceEditorTranslations.titlePlaceholder}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
              <SoftInput
                placeholder={serviceEditorTranslations.shortDescriptionPlaceholder}
                multiline
                rows={2}
                value={shortDescription}
                onChange={(event) => setShortDescription(event.target.value)}
              />
              <SoftInput
                placeholder={serviceEditorTranslations.seoDescriptionPlaceholder}
                multiline
                rows={4}
                value={seoDescription}
                onChange={(event) => setSeoDescription(event.target.value)}
              />
              <SoftInput
                placeholder={serviceEditorTranslations.descriptionPlaceholder}
                multiline
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
              <FormControl fullWidth>
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    {serviceEditorTranslations.pricingModeLabel}
                  </SoftTypography>
                </SoftBox>
                <Select
                  input={<SoftInput />}
                  value={priceMode}
                  onChange={(event) => {
                    const nextPriceMode = event.target.value as ServicePriceMode;
                    setPriceMode(nextPriceMode);

                    if (nextPriceMode !== 'RANGE') {
                      setPriceMax('');
                    }
                  }}
                >
                  <MenuItem value="FIXED">{serviceEditorTranslations.pricingModeFixed}</MenuItem>
                  <MenuItem value="FROM">{serviceEditorTranslations.pricingModeFrom}</MenuItem>
                  <MenuItem value="RANGE">{serviceEditorTranslations.pricingModeRange}</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    {serviceEditorTranslations.currencyLabel}
                  </SoftTypography>
                </SoftBox>
                <Select
                  input={<SoftInput />}
                  value={priceCurrency}
                  onChange={(event) =>
                    setPriceCurrency(event.target.value as ServicePriceCurrency)
                  }
                >
                  {CURRENCY_OPTIONS.map((currencyOption) => (
                    <MenuItem key={currencyOption.value} value={currencyOption.value}>
                      {currencyOption.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SoftInput
                type="number"
                placeholder={priceMinPlaceholder}
                value={priceMin}
                onChange={(event) => setPriceMin(event.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
              />
              {priceMode === 'RANGE' ? (
                <SoftInput
                  type="number"
                  placeholder={serviceEditorTranslations.maximumPricePlaceholder}
                  value={priceMax}
                  onChange={(event) => setPriceMax(event.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                />
              ) : null}
              <FormControl fullWidth>
                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    {serviceEditorTranslations.unitLabel}
                  </SoftTypography>
                </SoftBox>
                <Select
                  input={<SoftInput />}
                  value={priceUnit}
                  onChange={(event) => {
                    const nextPriceUnit = event.target.value as ServicePriceUnit | '';
                    setPriceUnit(nextPriceUnit);

                    if (nextPriceUnit !== 'CUSTOM') {
                      setPriceUnitCustom('');
                    }
                  }}
                >
                  <MenuItem value="">{serviceEditorTranslations.unitNone}</MenuItem>
                  <MenuItem value="HOUR">{serviceEditorTranslations.unitHour}</MenuItem>
                  <MenuItem value="DAY">{serviceEditorTranslations.unitDay}</MenuItem>
                  <MenuItem value="WEEK">{serviceEditorTranslations.unitWeek}</MenuItem>
                  <MenuItem value="MONTH">{serviceEditorTranslations.unitMonth}</MenuItem>
                  <MenuItem value="SESSION">{serviceEditorTranslations.unitSession}</MenuItem>
                  <MenuItem value="ITEM">{serviceEditorTranslations.unitItem}</MenuItem>
                  <MenuItem value="CUSTOM">{serviceEditorTranslations.unitCustom}</MenuItem>
                </Select>
              </FormControl>
              {priceUnit === 'CUSTOM' ? (
                <SoftInput
                  placeholder={serviceEditorTranslations.customUnitPlaceholder}
                  value={priceUnitCustom}
                  onChange={(event) => setPriceUnitCustom(event.target.value)}
                />
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <SoftButton
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleCloseCreateDialog}
              disabled={isSubmitting}
            >
              {serviceEditorTranslations.cancel}
            </SoftButton>
            <SoftButton
              type="submit"
              variant="gradient"
              color="info"
              disabled={
                isSubmitting ||
                !title.trim() ||
                !priceMin ||
                (priceMode === 'RANGE' && !priceMax)
              }
            >
              {isSubmitting
                ? serviceEditorTranslations.createSubmitting
                : serviceEditorTranslations.createSubmit}
            </SoftButton>
          </DialogActions>
        </SoftBox>
      </Dialog>
    </DashboardLayout>
  );
}

export default Services;
